'use client';

import { c as _c } from "react/compiler-runtime";
import type { FollowUpSuggestion } from '@/lib/chat/follow-up-suggestions';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import type { AiContext } from './types/ai-context';
import type { SidebarChatMessage } from './types/sidebar-chat-message';
import { ChatSuggestionRow } from './chat-suggestion-row';
import { CHAT_SUGGESTION_LIST_CLASS } from './chat-suggestions-skeleton';
import { getMessageTextContent } from './message-part-utils';
import { useChatFollowUps } from '@/data/chat/use-chat-follow-ups';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { buildDeterministicFollowUps } from '@/lib/chat/follow-up-suggestions';
import { parseTourFooter } from '@/lib/chat/tour-protocol';
import { getPrV2ComparisonFileVersions, readPrV2ViewedFilesForPr } from '@/lib/pr-v2/file-visibility-storage';
import { usePrStore } from '@/lib/stores/pr-store';
type FollowUpSuggestionChipsProps = {
  chatId: string;
  aiContext: AiContext;
  messages: SidebarChatMessage[];
  /** Hide the rows without unmounting, so the per-turn LLM query survives composer typing. */
  hidden?: boolean;
  onSend: (text: string) => void;
};

/**
 * Follow-up rows shown after an assistant answer completes:
 * - a deterministic "next file to review" navigation row computed from
 *   review-plan order minus the user's viewed files (instant, never stale);
 * - one LLM question row grounded in the last exchange (fades in ~2s later;
 *   silent failure leaves only the deterministic row).
 * Suppressed while a guided tour is active — the tour's own Next stop /
 * End tour pills own that slot.
 */
export function FollowUpSuggestionChips(t0) {
  const $ = _c(44);
  const {
    chatId,
    aiContext,
    messages,
    hidden: t1,
    onSend
  } = t0;
  const hidden = t1 === undefined ? false : t1;
  const {
    owner,
    repo,
    number,
    installationId
  } = usePrStore();
  let t2;
  if ($[0] !== messages) {
    t2 = messages.at(-1);
    $[0] = messages;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const lastMessage = t2;
  let t3;
  bb0: {
    if (lastMessage?.role !== "assistant") {
      t3 = false;
      break bb0;
    }
    let t4;
    if ($[2] !== lastMessage) {
      t4 = parseTourFooter(getMessageTextContent(lastMessage));
      $[2] = lastMessage;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    t3 = t4 !== null;
  }
  const isTourActive = t3;
  const contextFiles = aiContext.codeContent ?? aiContext.files;
  const contextHeadSha = aiContext.headSha;
  let t4;
  if ($[4] !== contextFiles) {
    t4 = contextFiles ?? [];
    $[4] = contextFiles;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== contextHeadSha || $[7] !== t4) {
    t5 = getPrV2ComparisonFileVersions(t4, contextHeadSha);
    $[6] = contextHeadSha;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const fileVersions = t5;
  let t6;
  if ($[9] !== fileVersions || $[10] !== number || $[11] !== owner || $[12] !== repo) {
    t6 = readPrV2ViewedFilesForPr({
      owner,
      repo,
      number
    }, fileVersions);
    $[9] = fileVersions;
    $[10] = number;
    $[11] = owner;
    $[12] = repo;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const viewedFiles = t6;
  let t7;
  bb1: {
    if (isTourActive) {
      let t8;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = [];
        $[14] = t8;
      } else {
        t8 = $[14];
      }
      t7 = t8;
      break bb1;
    }
    let t8;
    if ($[15] !== aiContext || $[16] !== viewedFiles) {
      t8 = buildDeterministicFollowUps({
        aiContext,
        viewedFiles
      });
      $[15] = aiContext;
      $[16] = viewedFiles;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t7 = t8;
  }
  const deterministicFollowUps = t7;
  let t8;
  if ($[18] !== aiContext || $[19] !== chatId || $[20] !== installationId || $[21] !== messages || $[22] !== viewedFiles) {
    t8 = {
      chatId,
      messages,
      aiContext,
      viewedFiles,
      installationId,
      prNumber: aiContext.pr.number
    };
    $[18] = aiContext;
    $[19] = chatId;
    $[20] = installationId;
    $[21] = messages;
    $[22] = viewedFiles;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const t9 = !isTourActive && lastMessage?.role === "assistant";
  let t10;
  if ($[24] !== t9) {
    t10 = {
      enabled: t9
    };
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const {
    data: llmFollowUps
  } = useChatFollowUps(t8, t10);
  let t11;
  bb2: {
    if (isTourActive) {
      let t12;
      if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = [];
        $[26] = t12;
      } else {
        t12 = $[26];
      }
      t11 = t12;
      break bb2;
    }
    let t12;
    if ($[27] !== deterministicFollowUps || $[28] !== llmFollowUps) {
      const merged = [...deterministicFollowUps];
      for (const suggestion of llmFollowUps ?? []) {
        if (suggestion.kind === "navigate" && merged.some(_temp)) {
          continue;
        }
        if (merged.some(existing_0 => existing_0.title === suggestion.title)) {
          continue;
        }
        merged.push({
          ...suggestion,
          kind: suggestion.kind ?? "question",
          source: "llm"
        });
      }
      t12 = merged.slice(0, 2);
      $[27] = deterministicFollowUps;
      $[28] = llmFollowUps;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    t11 = t12;
  }
  const followUps = t11;
  if (hidden || followUps.length === 0) {
    return null;
  }
  let t12;
  if ($[30] !== aiContext || $[31] !== fileVersions || $[32] !== installationId || $[33] !== number || $[34] !== onSend || $[35] !== owner || $[36] !== repo) {
    t12 = suggestion_0 => {
      let resolved = suggestion_0;
      if (suggestion_0.targetFilename) {
        const freshViewed = readPrV2ViewedFilesForPr({
          owner,
          repo,
          number
        }, fileVersions);
        if (freshViewed.includes(suggestion_0.targetFilename)) {
          const fresh = buildDeterministicFollowUps({
            aiContext,
            viewedFiles: freshViewed
          });
          resolved = fresh.find(candidate => candidate.kind === suggestion_0.kind) ?? {
            kind: "question",
            source: "deterministic",
            title: "What should I verify before sign-off?",
            action: "Every changed file is marked viewed \u2014 what should I double-check before signing off on this PR?"
          };
        }
      }
      trackClientEvent("v2_chat_follow_up_clicked", installationId?.toString(), {
        owner,
        repo,
        pr_number: number,
        follow_up_kind: resolved.kind,
        follow_up_source: resolved.source,
        target_filename: resolved.targetFilename
      });
      onSend(resolved.action);
    };
    $[30] = aiContext;
    $[31] = fileVersions;
    $[32] = installationId;
    $[33] = number;
    $[34] = onSend;
    $[35] = owner;
    $[36] = repo;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const sendFollowUp = t12;
  let t13;
  let t14;
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      opacity: 0,
      y: 4
    };
    t14 = {
      opacity: 1,
      y: 0
    };
    t15 = {
      duration: 0.15
    };
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t13 = $[38];
    t14 = $[39];
    t15 = $[40];
  }
  let t16;
  if ($[41] !== followUps || $[42] !== sendFollowUp) {
    t16 = <motion.div initial={t13} animate={t14} transition={t15} data-testid="chat-follow-ups" className={CHAT_SUGGESTION_LIST_CLASS}>{followUps.map((suggestion_1, index) => <ChatSuggestionRow key={`follow-up-${index}-${suggestion_1.title}`} kind={suggestion_1.kind} label={suggestion_1.title} onClick={() => sendFollowUp(suggestion_1)} />)}</motion.div>;
    $[41] = followUps;
    $[42] = sendFollowUp;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
}
function _temp(existing) {
  return existing.kind === "navigate";
}